import { ArrowRightOutlined, LinkOutlined } from '@ant-design/icons'
import assistantStore from '@stores/assistantStore'
import router from '@stores/router'
import { Grid, message, Spin } from 'antd'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { isStandaloneAssistant } from '../assistantBridge'
import { KW } from '../ui'

import {
  activeHeadingId,
  guideHeadings,
  guideLink,
  headingId,
  isPromptLink,
  nodeText,
} from './guideMarkdown'

/** Example prompt → a chip that puts the text in the composer (never sends it). */
const PromptChip = ({ text }: { text: string }) => (
  <button
    type="button"
    title="Put this in the message box"
    onClick={() => assistantStore.tryPrompt(text)}
    className="kw-guide-prompt"
  >
    {text}
    <ArrowRightOutlined className="text-[10px]" />
  </button>
)

/** On the standalone page the address bar follows the page / section being read (shareable). */
const syncUrl = (key: string, section?: string | null) => {
  if (!isStandaloneAssistant()) {
    return
  }
  const params = new URLSearchParams(router.location.search)
  params.delete('conversationId')
  params.set('guide', key)
  if (section) {
    params.set('section', section)
  } else {
    params.delete('section')
  }
  router.history.replace({ search: `?${params.toString()}` })
}

/**
 * The Assistant's user guide: a navigator of the pages the user may read (Getting started + one
 * per skill, with the open page's sections, highlighted as you scroll) and the page itself. Every
 * section heading has a link button that copies a shareable link to it.
 */
interface Props {
  /** On the standalone page there is room for a wider navigator (full section titles). */
  standalone?: boolean
}

const AssistantGuide = ({ standalone = false }: Props) => {
  const screens = Grid.useBreakpoint()
  const narrow = screens.md === false
  const contentRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)

  const { docs, guideKey } = assistantStore
  const doc = guideKey ? assistantStore.docPages[guideKey] : undefined
  const headings = useMemo(
    () => (doc ? guideHeadings(doc.markdown) : []),
    [doc]
  )

  const [activeId, setActiveId] = useState<string | null>(null)
  /** A section the user jumped to stays marked until they scroll by hand. */
  const pinnedRef = useRef<string | null>(null)
  /** True while the panel scrolls because of a jump (not the user). */
  const programmaticRef = useRef(false)
  const programmaticTimerRef = useRef<number>()

  useEffect(() => {
    assistantStore.loadDocs()
  }, [])

  useEffect(() => {
    if (guideKey) {
      assistantStore.loadDoc(guideKey)
      pinnedRef.current = null
      setActiveId(null)
      contentRef.current?.scrollTo({ top: 0 })
    }
  }, [guideKey])

  const goToSection = useCallback(
    (id: string, behavior: ScrollBehavior = 'smooth') => {
      const container = contentRef.current
      const target = container?.querySelector<HTMLElement>(`#${CSS.escape(id)}`)
      if (!container || !target) {
        return
      }
      pinnedRef.current = id
      setActiveId(id)
      programmaticRef.current = true
      // 'scrollend' ends the jump; the timer covers a jump that needs no scrolling.
      window.clearTimeout(programmaticTimerRef.current)
      programmaticTimerRef.current = window.setTimeout(() => {
        programmaticRef.current = false
      }, 1000)
      container.scrollTo({
        top:
          target.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          8,
        behavior,
      })
    },
    []
  )

  // Scroll-spy: mark the section being read.
  useEffect(() => {
    const container = contentRef.current
    if (!container || headings.length === 0) {
      return
    }
    let frame = 0
    const update = () => {
      frame = 0
      if (pinnedRef.current) {
        return
      }
      const containerTop = container.getBoundingClientRect().top
      const positions = headings
        .map(h => {
          const el = container.querySelector<HTMLElement>(
            `#${CSS.escape(h.id)}`
          )
          return el
            ? {
                id: h.id,
                top:
                  el.getBoundingClientRect().top -
                  containerTop +
                  container.scrollTop,
              }
            : null
        })
        .filter((p): p is { id: string; top: number } => p != null)
      setActiveId(
        activeHeadingId(
          positions,
          container.scrollTop,
          container.clientHeight,
          container.scrollHeight
        )
      )
    }
    // Any scroll the user makes — wheel, keys, touch or dragging the scrollbar — releases a
    // section they jumped to; the jump's own scroll does not.
    const onScroll = () => {
      if (!programmaticRef.current) {
        pinnedRef.current = null
      }
      if (!frame) {
        frame = requestAnimationFrame(update)
      }
    }
    const onScrollEnd = () => {
      programmaticRef.current = false
    }
    const release = () => {
      programmaticRef.current = false
      pinnedRef.current = null
    }
    container.addEventListener('scroll', onScroll, { passive: true })
    container.addEventListener('scrollend', onScrollEnd)
    container.addEventListener('wheel', release, { passive: true })
    container.addEventListener('touchstart', release, { passive: true })
    container.addEventListener('keydown', release)
    update()
    return () => {
      container.removeEventListener('scroll', onScroll)
      container.removeEventListener('scrollend', onScrollEnd)
      container.removeEventListener('wheel', release)
      container.removeEventListener('touchstart', release)
      container.removeEventListener('keydown', release)
      window.clearTimeout(programmaticTimerRef.current)
      if (frame) {
        cancelAnimationFrame(frame)
      }
    }
  }, [headings])

  // A section asked for from outside (link, /view/assistant?guide=…&section=…) once the page is in.
  useEffect(() => {
    const section = assistantStore.guideSection
    if (doc && section) {
      assistantStore.guideSection = null
      requestAnimationFrame(() => goToSection(section, 'auto'))
    }
  }, [doc, goToSection])

  // Keep the marked section visible in a long navigator.
  useEffect(() => {
    if (activeId) {
      navRef.current
        ?.querySelector(`[data-section="${CSS.escape(activeId)}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeId])

  const copySectionLink = useCallback(
    async (id: string) => {
      if (!guideKey) {
        return
      }
      goToSection(id)
      syncUrl(guideKey, id)
      try {
        await navigator.clipboard.writeText(
          guideLink(window.location.origin, guideKey, id)
        )
        // message.success('Link to this section copied')
      } catch {
        message.info('Copy the link from the address bar')
      }
    },
    [guideKey, goToSection]
  )

  /*
   * Guide pages are our own markdown (served by the API), rendered with the same safe subset as
   * answers: no raw HTML, no images; the only live links are the "#prompt" chips and the
   * section-link buttons.
   */
  const components = useMemo(
    () => ({
      img: () => null,
      a: ({ href, children }: any) =>
        isPromptLink(href) ? (
          <PromptChip text={nodeText(children)} />
        ) : (
          <span>{children}</span>
        ),
      h2: ({ children }: any) => {
        const id = headingId(nodeText(children))
        return (
          <h2 id={id} className="kw-guide-heading">
            {children}
            <button
              type="button"
              className="kw-guide-anchor"
              title="Copy a link to this section"
              aria-label={`Copy a link to "${nodeText(children)}"`}
              onClick={() => copySectionLink(id)}
            >
              <LinkOutlined />
            </button>
          </h2>
        )
      },
    }),
    [copySectionLink]
  )

  const pageButton = (key: string, title: string) => {
    const active = key === guideKey
    return (
      <button
        key={key}
        type="button"
        onClick={() => {
          if (active) {
            pinnedRef.current = null
            contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
          } else {
            assistantStore.openGuide(key)
          }
          syncUrl(key)
        }}
        className="kw-guide-nav-item"
        style={{
          color: active ? KW.teal500 : KW.ink,
          background: active ? KW.soft : 'transparent',
          borderColor: active ? KW.softBorder : 'transparent',
        }}
        aria-current={active ? 'page' : undefined}
      >
        {title}
      </button>
    )
  }

  return (
    <div
      className={`flex min-h-0 flex-1 ${narrow ? 'flex-col' : 'flex-row'}`}
      aria-label="Assistant guide"
    >
      <nav
        ref={navRef}
        className={
          narrow
            ? 'flex flex-shrink-0 gap-1 overflow-x-auto px-3 py-2'
            : `${
                standalone ? 'w-[256px]' : 'w-[184px]'
              } flex-shrink-0 overflow-y-auto px-2 py-3`
        }
        style={{
          background: KW.soft2,
          [narrow ? 'borderBottom' : 'borderRight']: `1px solid ${KW.line}`,
        }}
      >
        {!narrow && (
          <div
            className="mb-2 px-2 text-[11px] font-extrabold uppercase tracking-widest"
            style={{ color: KW.muted }}
          >
            Guide
          </div>
        )}
        {docs.map(item => (
          <div key={item.key} className={narrow ? 'flex-shrink-0' : 'mb-[2px]'}>
            {pageButton(item.key, item.title)}
            {!narrow && item.key === guideKey && headings.length > 0 && (
              <div className="mb-1 ml-2 mt-[2px]">
                {headings.map(h => {
                  const current = h.id === activeId
                  return (
                    <button
                      key={h.id}
                      type="button"
                      data-section={h.id}
                      aria-current={current ? 'location' : undefined}
                      onClick={() => {
                        goToSection(h.id)
                        syncUrl(item.key, h.id)
                      }}
                      className={`kw-guide-nav-section${
                        current ? ' is-active' : ''
                      }`}
                    >
                      {h.title}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div
        ref={contentRef}
        tabIndex={-1}
        className="min-w-0 flex-1 overflow-y-auto px-5 py-4 outline-none"
      >
        {doc ? (
          <div className="kw-md kw-guide">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {doc.markdown}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="flex justify-center pt-10">
            <Spin />
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(AssistantGuide)
